import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { UserPlus, Link2, Mail, Share2, Copy, Check, Loader2, MessageCircle, Users, QrCode, Sparkles, ListChecks } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';
import { useMembership } from '@/contexts/MembershipContext';
import { useAuth } from '@/contexts/AuthContext';
import SentInvitesPanel from '@/components/SentInvitesPanel';
import {
  hashContactIdentity,
  normalizePhoneToE164,
} from '@/lib/contactIdentity';
import {
  projectMembershipSeatAuthority,
  type MembershipPlanCapacity,
  type MembershipSeatAuthorityReport,
} from '@/governor/membership/MembershipSeatAuthority';
import {
  decideInviteCreation,
  type MembershipInviteType,
} from '@/governor/membership/MembershipInviteLifecycle';


interface InviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional name of a specific person the inviter wants to bring into the
   * circle — used when the invite is initiated from a person's profile card
   * ("Invite this person"). Pre-fills the inviter's personal note.
   */
  prefillPersonName?: string | null;
  /**
   * Optional relationship purpose — drives the "invite with purpose"
   * targeted-message system. Examples: 'cousin', 'mother', 'aunt-uncle'.
   * When set, the personal note pre-fills with a role-specific message
   * like "You're listed as my cousin — join to complete our family tree."
   */
  prefillRelation?: string | null;
  targetMemberId?: string | null;
  initialInviteType?: MembershipInviteType;
}

const randomToken = () => {
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
};

/** Map a relationship value to a short, friendly noun for the message. */
const relationLabel = (rel?: string | null): string | null => {
  if (!rel) return null;
  const map: Record<string, string> = {
    mother: 'mother',
    father: 'father',
    'step-parent': 'parent',
    'adopted-parent': 'parent',
    sibling: 'sibling',
    spouse: 'spouse',
    child: 'child',
    grandparent: 'grandparent',
    'great-grandparent': 'great-grandparent',
    'aunt-uncle': 'aunt or uncle',
    cousin: 'cousin',
    'cousin-child': 'niece or nephew',
  };
  return map[rel] ?? null;
};

const InviteModal: React.FC<InviteModalProps> = ({
  open,
  onOpenChange,
  prefillPersonName,
  prefillRelation,
  targetMemberId,
  initialInviteType = 'FAMILY_PROFILE',
}) => {
  const { memberEmail, plan } = useMembership();
  const { user, memberName } = useAuth();
  const [inviterName, setInviterName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  // Bumped after every successful recordInvite() so the Sent panel
  // re-fetches without the user having to manually refresh.
  const [sentRefreshKey, setSentRefreshKey] = useState(0);
  const [inviteType, setInviteType] =
    useState<MembershipInviteType>('FAMILY_PROFILE');
  const [eligibleTargets, setEligibleTargets] = useState<Array<{
    id: string;
    name: string;
    relationship: string | null;
  }>>([]);
  const [selectedTargetMemberId, setSelectedTargetMemberId] =
    useState<string | null>(targetMemberId ?? null);

  const sponsorshipLocked =
    initialInviteType === 'SPONSORED_SEAT';

  const effectiveInviteType: MembershipInviteType =
    sponsorshipLocked
      ? 'SPONSORED_SEAT'
      : inviteType;

  const resolvedTargetMemberId =
    targetMemberId ?? selectedTargetMemberId;

  const personTargeted =
    Boolean(resolvedTargetMemberId);
  const [seatAuthority, setSeatAuthority] =
    useState<MembershipSeatAuthorityReport | null>(null);
  const [seatRuntimeLoading, setSeatRuntimeLoading] =
    useState(false);
  const [seatSchemaAvailable, setSeatSchemaAvailable] =
    useState(false);


  // Pre-fill inviter name from auth when available so the link is meaningful
  // out of the box. Pre-fill the personal message when this modal was opened
  // for a specific person ("Invite this person to KinsCircle") OR for a
  // specific relationship purpose ("invite a cousin").
  useEffect(() => {
    if (open) {
      setInviteType(initialInviteType);
      if (memberName && !inviterName) setInviterName(memberName);
      const role = relationLabel(prefillRelation);
      if (prefillPersonName) {
        // Person-specific (highest priority): targeted "I added you to our tree".
        const firstName = prefillPersonName.split(' ')[0];
        if (role) {
          setPersonalMessage(
            `Hi ${firstName} — I added you to our family tree on KinsCircle as my ${role}. Join to complete it.`,
          );
        } else {
          setPersonalMessage(
            `Hi ${firstName} — I added you to our family tree on KinsCircle. Join to complete it.`,
          );
        }
      } else if (role) {
        // Role-only: "you're listed as my cousin — join to complete our family tree."
        setPersonalMessage(
          `Hi! I added you to our family tree on KinsCircle as my ${role}. Join to complete it.`,
        );
      } else if (!personalMessage) {
        // Generic default — matches the "invite with purpose" copy.
        setPersonalMessage(
          `I added you to our family tree on KinsCircle — join to complete it.`,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    memberName,
    prefillPersonName,
    prefillRelation,
    initialInviteType,
  ]);

  useEffect(() => {
    if (!open) return;
    setSelectedTargetMemberId(
      targetMemberId ?? null,
    );
  }, [open, targetMemberId]);

  const resolvePlanCapacity = (): MembershipPlanCapacity => {
    switch (plan) {
      case 'monthly_premium':
        return 5;
      case 'annual_founding':
        return 10;
      case 'annual_standard':
        return 30;
      default:
        return 1;
    }
  };

  useEffect(() => {
    if (!open || !user?.id) {
      setSeatAuthority(null);
      setSeatSchemaAvailable(false);
      return;
    }

    let cancelled = false;

    const loadSeatAuthority = async () => {
      setSeatRuntimeLoading(true);

      const planCapacity = resolvePlanCapacity();

      const { data, error } = await supabase
        .from('invites')
        .select(
          'invite_type,status,accepted_user_id,seat_reservation_expires_at,seat_returned_at',
        )
        .eq('inviter_user_id', user.id)
        .eq('invite_type', 'SPONSORED_SEAT');

      if (cancelled) return;

      if (error) {
        setSeatSchemaAvailable(false);
        setSeatAuthority(
          projectMembershipSeatAuthority({
            planCapacity,
          }),
        );
        setSeatRuntimeLoading(false);
        return;
      }

      const now = Date.now();

      const activeSponsoredSeats = (data ?? []).filter(
        (row) =>
          Boolean(row.accepted_user_id) &&
          !row.seat_returned_at &&
          row.status !== 'cancelled' &&
          row.status !== 'rejected',
      ).length;

      const reservedSponsoredSeats = (data ?? []).filter(
        (row) =>
          !row.accepted_user_id &&
          !row.seat_returned_at &&
          Boolean(row.seat_reservation_expires_at) &&
          new Date(
            row.seat_reservation_expires_at,
          ).getTime() > now &&
          ['sent', 'opened', 'reserved'].includes(
            row.status,
          ),
      ).length;

      setSeatSchemaAvailable(true);
      setSeatAuthority(
        projectMembershipSeatAuthority({
          planCapacity,
          activeSponsoredSeats,
          reservedSponsoredSeats,
        }),
      );
      setSeatRuntimeLoading(false);
    };

    void loadSeatAuthority();

    return () => {
      cancelled = true;
    };
  }, [open, user?.id, plan]);

  useEffect(() => {
    if (!open || !user?.id) {
      setEligibleTargets([]);
      return;
    }

    let cancelled = false;

    const loadTargets = async () => {
      const { data, error } = await supabase
        .from('family_graph_members')
        .select(
          'id,name,display_name,nickname,relationship,side,approval_status,created_at',
        )
        .eq('owner_user_id', user.id)
        .is('claimed_by_user_id', null)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });

      if (cancelled) return;

      if (error) {
        setEligibleTargets([]);
        return;
      }

      const rows = (data ?? [])
        .filter(
          (row: any) =>
            row.approval_status !== 'rejected' &&
            !(
              row.side === 'self' &&
              ['unknown', 'self'].includes(
                row.relationship ?? '',
              )
            ),
        )
        .map((row: any) => ({
          id: String(row.id),
          name:
            row.name ||
            row.display_name ||
            row.nickname ||
            'Family member',
          relationship:
            row.relationship ?? null,
        }));

      setEligibleTargets(rows);
    };

    void loadTargets();

    return () => {
      cancelled = true;
    };
  }, [open, user?.id]);

  // Stable per-modal-session secure token. Used to:
  //   1. Embed in the invite URL as ?t=<token>
  //   2. Tag the `invites` row so we can match opened/accepted events
  //      back to a specific invite (lifecycle tracking).
  const [token, setToken] =
    useState(() => randomToken());

  useEffect(() => {
    if (open) {
      setToken(randomToken());
    }
  }, [open]);

  // Build the canonical, stable user-id-based invite URL with a secure
  // per-invite token: https://<host>/invite/{user_id}?t={token}
  // When the user isn't signed in yet, fall back to a token-only deep
  // link (legacy behavior) so the modal still produces a usable link.
  const inviteLink = useMemo(() => {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://www.kinscircleapp.com';
    if (user?.id) {
      return `${base}/invite/${user.id}?t=${token}`;
    }
    const params = new URLSearchParams({ invite: token });
    if (memberEmail) params.set('from', memberEmail);
    return `${base}/?${params.toString()}`;
  }, [user?.id, token, memberEmail]);

  const qrSrc = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(inviteLink)}`,
    [inviteLink],
  );

  type InviteRecordChannel =
    | 'email'
    | 'sms'
    | 'link'
    | 'share'
    | 'contacts';

  interface InviteRecordTarget {
    email?: string | null;
    phone?: string | null;
  }

  const isMissingInviteLifecycleSchema = (
    message: string,
  ): boolean => {
    const value = message.toLowerCase();

    return (
      value.includes('schema cache') ||
      value.includes('invite_type') ||
      value.includes('delivery_channel') ||
      value.includes('recipient_phone_e164') ||
      value.includes('recipient_phone_hash') ||
      value.includes('recipient_email_hash') ||
      value.includes('lifecycle_version')
    );
  };

  const recordInvite = async (
    channel: InviteRecordChannel,
    target: InviteRecordTarget = {},
  ): Promise<boolean> => {
    try {
      const currentSeatAuthority =
        seatAuthority ??
        projectMembershipSeatAuthority({
          planCapacity: resolvePlanCapacity(),
        });

      const creationDecision = decideInviteCreation({
        inviteType: effectiveInviteType,
        deliveryChannel: channel,
        seatAuthority: currentSeatAuthority,
      });

      if (!creationDecision.allowed) {
        toast.error(
          creationDecision.reason ===
            'SPONSORED_CAPACITY_FULL'
            ? 'All membership seats are currently occupied or reserved.'
            : 'The membership seat state could not be verified.',
        );
        return false;
      }

      if (
        effectiveInviteType === 'SPONSORED_SEAT' &&
        !seatSchemaAvailable
      ) {
        toast.error(
          'Sponsored invitations will be available after the membership database activation is completed.',
        );
        return false;
      }
      const normalizedEmail =
        target.email?.trim().toLowerCase() ||
        recipientEmail.trim().toLowerCase() ||
        null;

      const phoneResult = target.phone
        ? normalizePhoneToE164(target.phone)
        : null;

      const normalizedPhone =
        phoneResult?.valid
          ? phoneResult.identity.e164
          : target.phone?.trim() || null;

      const expiresAt =
        creationDecision.inviteExpiresAt;

      const legacyPayload = {
        inviter_user_id: user?.id ?? null,
        inviter_email: memberEmail || null,
        inviter_name: inviterName || memberName || null,
        recipient_email: normalizedEmail,
        recipient_phone: normalizedPhone,
        channel,
        token,
        status: 'sent',
        expires_at: expiresAt,
      };

      const recipientEmailHash = normalizedEmail
        ? await hashContactIdentity(normalizedEmail)
        : null;

      const recipientPhoneHash =
        phoneResult?.valid
          ? await hashContactIdentity(
              phoneResult.identity.e164,
            )
          : null;

      const extendedPayload = {
        ...legacyPayload,
        invite_type: effectiveInviteType,
        delivery_channel: channel,
        recipient_phone_e164:
          phoneResult?.valid
            ? phoneResult.identity.e164
            : null,
        recipient_phone_hash: recipientPhoneHash,
        recipient_email_hash: recipientEmailHash,
        membership_owner_user_id:
          effectiveInviteType === 'SPONSORED_SEAT'
            ? user?.id ?? null
            : null,
        seat_reservation_expires_at:
          creationDecision.seatReservationExpiresAt,
        lifecycle_version: 1,
        status: creationDecision.initialStatus.toLowerCase(),
      };

      if (personTargeted) {
        if (!resolvedTargetMemberId) {
          toast.error(
            'Choose the exact family placecard before sending this invitation.',
          );
          return false;
        }

        const hasEmail = Boolean(normalizedEmail);
        const hasPhone = Boolean(phoneResult?.valid);

        if (hasEmail === hasPhone) {
          toast.error(
            'A targeted invitation requires exactly one email address or phone number.',
          );
          return false;
        }

        const committedChannel: InviteRecordChannel =
          hasEmail ? 'email' : 'sms';

        if (channel !== committedChannel) {
          toast.error(
            'The delivery channel does not match the committed recipient identity.',
          );
          return false;
        }

        const {
          data: targetedInvite,
          error: targetedError,
        } = await supabase.rpc(
          'issue_person_targeted_invite',
          {
            p_target_member_id:
              resolvedTargetMemberId,
            p_invite_type:
              effectiveInviteType,
            p_recipient_email:
              normalizedEmail,
            p_recipient_phone:
              phoneResult?.valid
                ? phoneResult.identity.e164
                : null,
            p_delivery_channel:
              committedChannel,
            p_token: token,
            p_inviter_name:
              inviterName ||
              memberName ||
              null,
            p_inviter_email:
              memberEmail || null,
          },
        );

        if (targetedError) {
          throw targetedError;
        }

        if (
          !targetedInvite ||
          targetedInvite.person_targeted !== true ||
          targetedInvite.target_member_id !==
            resolvedTargetMemberId ||
          targetedInvite.token !== token
        ) {
          throw new Error(
            'TARGETED_INVITE_COMMIT_MISMATCH',
          );
        }

        setSentRefreshKey(
          (value) => value + 1,
        );

        setToken(randomToken());

        if (
          effectiveInviteType ===
          'SPONSORED_SEAT'
        ) {
          setSeatAuthority(
            projectMembershipSeatAuthority({
              planCapacity:
                currentSeatAuthority.planCapacity,
              activeSponsoredSeats:
                currentSeatAuthority
                  .activeSponsoredSeatCount,
              reservedSponsoredSeats:
                currentSeatAuthority
                  .reservedSponsoredSeatCount + 1,
              returnedSeatCount:
                currentSeatAuthority
                  .returnedSeatCount,
              familyInvitesSent:
                currentSeatAuthority
                  .familyInvitesSent,
              familyInvitesPending:
                currentSeatAuthority
                  .familyInvitesPending,
            }),
          );
        }

        return true;
      }

      if (
        effectiveInviteType ===
        'SPONSORED_SEAT'
      ) {
        toast.error(
          'Choose the exact person on your Tree before reserving a sponsored seat.',
        );
        return false;
      }

      const { error: extendedError } = await supabase
        .from('invites')
        .insert(extendedPayload);

      if (extendedError) {
        if (
          !isMissingInviteLifecycleSchema(
            extendedError.message || '',
          )
        ) {
          throw extendedError;
        }

        if (effectiveInviteType === 'SPONSORED_SEAT') {
          throw new Error(
            'Sponsored invitation schema is not active.',
          );
        }

        const { error: legacyError } = await supabase
          .from('invites')
          .insert(legacyPayload);

        if (legacyError) {
          throw legacyError;
        }
      }

      setSentRefreshKey((value) => value + 1);

      return true;
    } catch (error) {
      console.error(
        '[InviteModal] invite persistence failed',
        error,
      );
      toast.error(
        effectiveInviteType === 'SPONSORED_SEAT'
          ? 'The sponsored seat was not reserved, so KinsCircle blocked delivery.'
          : 'The invite opened, but KinsCircle could not record it.',
      );
      return false;
    }
  };

  /**
   * Soft, dismissible "Are you inviting a family member?" confirmation.
   * Per spec: guidance only, NOT enforcement — single-step, fast,
   * dismissible. We deliberately use the native confirm() so we don't
   * introduce extra UI/components or block invite growth.
   */
  const confirmFamilyOnly = (): boolean => {
    if (typeof window === 'undefined') return true;
    return window.confirm(
      'Are you inviting a family member?\n\nKinsCircle invites are for family only — for public sharing, use the public link instead.',
    );
  };

  // Internal copy helper that does NOT prompt — used by handleNativeShare's
  // fallback so we never double-prompt the user.
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Invite link copied!');
      recordInvite('link');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleCopy = async () => {
    if (
      personTargeted ||
      effectiveInviteType === 'SPONSORED_SEAT'
    ) {
      toast.error(
        personTargeted
          ? 'Person-targeted invitations must be sent to the committed email or phone.'
          : 'Sponsored seats must be sent to one identified person.',
      );
      return;
    }

    if (!confirmFamilyOnly()) return;
    await doCopy();
  };

  const handleNativeShare = async () => {
    if (
      personTargeted ||
      effectiveInviteType === 'SPONSORED_SEAT'
    ) {
      toast.error(
        personTargeted
          ? 'Use Email, SMS, or one Contact so the exact person remains bound to this invitation.'
          : 'Use Email or Contacts for a sponsored seat.',
      );
      return;
    }

    if (!confirmFamilyOnly()) return;
    const shareData = {
      title: 'Join my family on KinsCircle',
      text: `${inviterName || 'Your family'} invited you to KinsCircle — join my family and help build our shared legacy.`,
      url: inviteLink,
    };
    // @ts-expect-error - navigator.share exists on supported platforms
    if (navigator.share) {
      try {
        // @ts-expect-error
        await navigator.share(shareData);
        recordInvite('share');
      } catch {
        // user cancelled - no-op
      }
    } else {
      await doCopy();
      toast('Share not supported here — link copied instead');
    }
  };

  const handleContacts = async () => {
    if (!confirmFamilyOnly()) return;

    // Contact Picker API (mobile Chrome/Android).
    // @ts-expect-error
    const contactsApi = navigator.contacts;

    const exactRecipientRequired =
      personTargeted ||
      effectiveInviteType === 'SPONSORED_SEAT';

    if (
      contactsApi &&
      typeof contactsApi.select === 'function'
    ) {
      try {
        const picked = await contactsApi.select(
          ['name', 'email', 'tel'],
          { multiple: !exactRecipientRequired },
        );

        if (!picked || picked.length === 0) {
          return;
        }

        if (
          exactRecipientRequired &&
          picked.length !== 1
        ) {
          toast.error(
            'Choose exactly one person for this invitation.',
          );
          return;
        }

        const emails = picked
          .flatMap(
            (contact: { email?: string[] }) =>
              contact.email || [],
          )
          .filter(Boolean);

        const phones = picked
          .flatMap(
            (contact: { tel?: string[] }) =>
              contact.tel || [],
          )
          .filter(Boolean);

        const committedTarget = emails[0]
          ? { email: emails[0], phone: null }
          : phones[0]
            ? { email: null, phone: phones[0] }
            : null;

        if (!committedTarget) {
          toast.error(
            'The selected contact needs an email address or phone number.',
          );
          return;
        }

        const committedChannel:
          InviteRecordChannel =
            committedTarget.email
              ? 'email'
              : 'sms';

        const recorded = await recordInvite(
          exactRecipientRequired
            ? committedChannel
            : 'contacts',
          committedTarget,
        );

        if (!recorded) {
          return;
        }

        if (committedTarget.email) {
          const subject = encodeURIComponent(
            effectiveInviteType ===
              'SPONSORED_SEAT'
              ? `${inviterName || 'Your family'} reserved a KinsCircle membership seat for you`
              : `${inviterName || 'Your family'} invited you to KinsCircle`,
          );

          const body = encodeURIComponent(
            `${personalMessage ? personalMessage + '\n\n' : ''}${
              effectiveInviteType === 'SPONSORED_SEAT'
                ? `${inviterName || 'Your family'} reserved a personal KinsCircle membership seat for you.`
                : `${inviterName || 'Your family'} invited you to KinsCircle.`
            }\n\n${inviteLink}`,
          );

          window.location.href =
            `mailto:${committedTarget.email}?subject=${subject}&body=${body}`;
        } else if (committedTarget.phone) {
          const body = encodeURIComponent(
            `${
              effectiveInviteType ===
              'SPONSORED_SEAT'
                ? `${inviterName || 'Your family'} reserved a personal KinsCircle membership seat for you.`
                : `${inviterName || 'Your family'} invited you to KinsCircle.`
            } ${inviteLink}`,
          );

          window.location.href =
            `sms:${committedTarget.phone}?body=${body}`;
        }

        toast.success(
          exactRecipientRequired
            ? 'Opening the private invitation for this person'
            : `Opening invite for ${picked.length} contact${picked.length === 1 ? '' : 's'}`,
        );
      } catch {
        toast.error('Could not access contacts');
      }

      return;
    }

    if (exactRecipientRequired) {
      toast.error(
        'Use the Email or SMS option on this device for this person-specific invitation.',
      );
      return;
    }

    toast(
      'Contact picker only works on mobile Chrome — using share instead',
    );

    await handleNativeShare();
  };

  const handleSendSms = async () => {
    const phoneResult =
      normalizePhoneToE164(recipientPhone);

    if (!phoneResult.valid) {
      toast.error(
        'Enter a valid mobile phone number.',
      );
      return;
    }

    if (!confirmFamilyOnly()) return;

    setSending(true);

    try {
      const recorded = await recordInvite(
        'sms',
        {
          phone: phoneResult.identity.e164,
        },
      );

      if (!recorded) return;

      const body = encodeURIComponent(
        `${inviterName || 'Your family'} invited you to KinsCircle: ${inviteLink}`,
      );

      window.location.href =
        `sms:${phoneResult.identity.e164}?body=${body}`;

      setRecipientPhone('');
    } finally {
      setSending(false);
    }
  };

  const openMailtoFallback = (email: string) => {
    const subject = encodeURIComponent(`${inviterName || 'Your family'} invited you to KinsCircle`);
    const body = encodeURIComponent(
      `${personalMessage ? personalMessage + '\n\n' : ''}Hi!\n\n${inviterName || 'Your family'} invited you to join our private family circle on KinsCircle — photos, stories, the family tree, and more, all in one place.\n\nJoin here:\n${inviteLink}\n\nSee you inside!`,
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleSendEmail = async () => {
    const email = recipientEmail.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!confirmFamilyOnly()) return;

    setSending(true);
    let inviteRecorded = false;

    try {
      if (
        personTargeted ||
        effectiveInviteType === 'SPONSORED_SEAT'
      ) {
        inviteRecorded = await recordInvite('email', {
          email,
        });

        if (!inviteRecorded) {
          return;
        }
      }

      // Try the edge function first (uses the Resend HTTP API server-side)
      const { data, error } = await supabase.functions.invoke('send-invite', {
        body: {
          inviterName: inviterName || undefined,
          inviterEmail: memberEmail || undefined,
          recipientEmail: email,
          inviteLink,
          personalMessage: personalMessage || undefined,
        },
      });

      if (!inviteRecorded) {
        inviteRecorded = await recordInvite('email', {
          email,
        });
      }

      if (!inviteRecorded) {
        return;
      }

      if (!error && data?.sent) {
        toast.success(`Invitation sent to ${email}`);
        setRecipientEmail('');
        setPersonalMessage('');
      } else {
        // Fallback: open the user's email client with the invite pre-filled
        toast('Opening your email app…', {
          description: 'We\'ll pre-fill the invite so you can send it directly.',
        });
        openMailtoFallback(email);
        setRecipientEmail('');
        setPersonalMessage('');
      }
    } catch {
      // Network or function error — always fall back to mailto so the user can still invite
      if (!inviteRecorded) {
        inviteRecorded = await recordInvite('email', {
          email,
        });
      }

      if (!inviteRecorded) {
        return;
      }

      toast('Opening your email app…', {
        description: 'We\'ll pre-fill the invite so you can send it directly.',
      });
      openMailtoFallback(email);
      setRecipientEmail('');
      setPersonalMessage('');
    } finally {
      setSending(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-[#FAF6F0] to-[#F5EDE0] border-amber-300 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-amber-950 flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-amber-800" />
            Invite Family to KinsCircle

          </DialogTitle>
          <DialogDescription className="text-amber-800/70">
            Bring your people into your private family network — partners, in-laws, step-family, every generation. Share by email, contacts, or a private link.
          </DialogDescription>
        </DialogHeader>


        <div className="space-y-4">

          {/* Subtle, non-blocking family-only reminder. Per spec: small,
              clean, never interrupts the flow. */}
          <div className="rounded-md border border-amber-200/70 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900/80 leading-snug">
            Only invite people who belong to your family. For public sharing,
            use the public link instead.
          </div>


          <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-violet-900">
              Person on your Tree
              {effectiveInviteType === 'SPONSORED_SEAT' ? ' — required' : ''}
            </label>
            <select
              value={resolvedTargetMemberId ?? ''}
              onChange={(event) =>
                setSelectedTargetMemberId(
                  event.target.value || null,
                )
              }
              disabled={Boolean(targetMemberId)}
              className="mt-2 w-full rounded-md border border-violet-300 bg-white px-3 py-2 text-sm text-slate-900"
            >
              <option value="">
                General family invite — only for someone not already on the Tree
              </option>
              {targetMemberId &&
                !eligibleTargets.some(
                  (item) => item.id === targetMemberId,
                ) && (
                  <option value={targetMemberId}>
                    {prefillPersonName || 'Selected family member'}
                  </option>
                )}
              {eligibleTargets.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                  {item.relationship
                    ? ` — ${item.relationship}`
                    : ''}
                </option>
              ))}
            </select>
            <p className="mt-2 text-[11px] leading-relaxed text-violet-900/75">
              Selecting a placecard makes the invitation person-specific. KinsCircle will bind the recipient only to that exact card after verified email or phone control.
            </p>
          </div>

          <div className="rounded-xl border border-amber-200 bg-white p-3 space-y-3">
            {sponsorshipLocked && (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-950">
                <div className="font-bold">
                  Sponsored seats are sent one person at a time.
                </div>
                <div className="mt-1 text-xs leading-relaxed">
                  Each invitation reserves one paid membership seat for one family member for seven days. Group messages, shared links, and multiple-contact selection cannot reserve sponsored seats.
                </div>
              </div>
            )}
            <div>
              <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Invitation type
              </div>
              <div className="text-[11px] text-amber-800/70 mt-1">
                Family invites grow the tree. Sponsored invites reserve one membership seat for seven days.
              </div>
            </div>

            {!sponsorshipLocked && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={
                  inviteType === 'FAMILY_PROFILE'
                    ? 'default'
                    : 'outline'
                }
                onClick={() =>
                  setInviteType('FAMILY_PROFILE')
                }
                className={
                  inviteType === 'FAMILY_PROFILE'
                    ? 'bg-amber-800 hover:bg-amber-900'
                    : 'border-amber-300 text-amber-900'
                }
              >
                Join Our Family
              </Button>

              <Button
                type="button"
                variant={
                  effectiveInviteType === 'SPONSORED_SEAT'
                    ? 'default'
                    : 'outline'
                }
                onClick={() => {
                  if (!seatSchemaAvailable) {
                    toast.error(
                      'Sponsored invitations are waiting for database activation.',
                    );
                    return;
                  }

                  if (
                    !seatAuthority?.canReserveSponsoredSeat
                  ) {
                    toast.error(
                      'No sponsored membership seats are currently available.',
                    );
                    return;
                  }

                  setInviteType('SPONSORED_SEAT');
                }}
                disabled={
                  seatRuntimeLoading ||
                  !seatSchemaAvailable ||
                  !seatAuthority?.canReserveSponsoredSeat
                }
                className={
                  effectiveInviteType === 'SPONSORED_SEAT'
                    ? 'bg-emerald-700 hover:bg-emerald-800'
                    : 'border-emerald-300 text-emerald-800'
                }
              >
                Claim a Seat
              </Button>
            </div>
            )}

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-amber-50 p-2">
                <div className="font-bold text-amber-950">
                  {seatAuthority?.planCapacity ?? resolvePlanCapacity()}
                </div>
                <div className="text-[10px] uppercase text-amber-800/70">
                  Total
                </div>
              </div>

              <div className="rounded-lg bg-blue-50 p-2">
                <div className="font-bold text-blue-900">
                  {seatAuthority?.occupiedSeatCount ?? 1}
                </div>
                <div className="text-[10px] uppercase text-blue-800/70">
                  Used
                </div>
              </div>

              <div className="rounded-lg bg-emerald-50 p-2">
                <div className="font-bold text-emerald-900">
                  {seatAuthority?.availableSeatCount ?? 0}
                </div>
                <div className="text-[10px] uppercase text-emerald-800/70">
                  Available
                </div>
              </div>
            </div>

            {!seatSchemaAvailable && (
              <div className="text-[11px] text-amber-800/70">
                Family invitations remain available. Sponsored-seat activation is pending the approved database migration.
              </div>
            )}

            {effectiveInviteType === 'SPONSORED_SEAT' && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
                This personal invitation reserves one paid membership seat for one identified family member for seven days. It cannot be transferred or sent as a group message. If it is not claimed, the seat returns automatically.
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Your name (shown on the invite)</label>
            <Input
              value={inviterName}
              onChange={e => setInviterName(e.target.value)}
              placeholder="e.g. Aunt Rosa"
              className="mt-1 bg-white border-amber-300"
            />
          </div>

          <Tabs defaultValue={personTargeted ? "contacts" : "link"} className="w-full">
            <TabsList className="grid grid-cols-5 bg-amber-100/60">
              <TabsTrigger value="link" disabled={personTargeted || effectiveInviteType === 'SPONSORED_SEAT'} className="gap-1.5 data-[state=active]:bg-white">
                <Link2 className="w-4 h-4" /> Link
              </TabsTrigger>
              <TabsTrigger value="email" className="gap-1.5 data-[state=active]:bg-white">
                <Mail className="w-4 h-4" /> Email
              </TabsTrigger>
              <TabsTrigger value="sms" className="gap-1.5 data-[state=active]:bg-white">
                <MessageCircle className="w-4 h-4" /> SMS
              </TabsTrigger>
              <TabsTrigger value="contacts" className="gap-1.5 data-[state=active]:bg-white">
                <Users className="w-4 h-4" /> Contacts
              </TabsTrigger>
              <TabsTrigger value="sent" className="gap-1.5 data-[state=active]:bg-white">
                <ListChecks className="w-4 h-4" /> Sent
              </TabsTrigger>
            </TabsList>


            <TabsContent value="link" className="space-y-3 pt-4">
              <p className="text-sm text-amber-900/80">
                Copy your private invite link and share it anywhere — WhatsApp, Messenger, AirDrop, Signal, you name it.
              </p>
              <div className="flex gap-2">
                <Input readOnly value={inviteLink} className="bg-white border-amber-300 text-xs font-mono text-amber-950" />
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  className="shrink-0 border-amber-400 text-amber-900 hover:bg-amber-100 gap-1.5"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <Button
                onClick={handleNativeShare}
                className="w-full bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50 gap-2"
              >
                <Share2 className="w-4 h-4" /> Share link…
              </Button>

              <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-amber-200">
                <img src={qrSrc} alt="Invite QR code" className="w-24 h-24 rounded-md border border-amber-200" />
                <div className="text-xs text-amber-900/80">
                  <div className="flex items-center gap-1.5 font-semibold text-amber-950 mb-1">
                    <QrCode className="w-3.5 h-3.5" /> Scan to join
                  </div>
                  Hold your phone camera over this code — it'll open the invite instantly.
                </div>
              </div>
            </TabsContent>

            <TabsContent value="email" className="space-y-3 pt-4">
              <div>
                <label className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Family member's email</label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={e => setRecipientEmail(e.target.value)}
                  placeholder="grandma@example.com"
                  className="mt-1 bg-white border-amber-300"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-amber-900 uppercase tracking-wider flex items-center gap-1">
                  <MessageCircle className="w-3.5 h-3.5" /> Personal note (optional)
                </label>
                <Textarea
                  value={personalMessage}
                  onChange={e => setPersonalMessage(e.target.value)}
                  placeholder="Can't wait to share our photos with you!"
                  rows={3}
                  className="mt-1 bg-white border-amber-300 resize-none"
                />
              </div>
              <Button
                onClick={handleSendEmail}
                disabled={sending || !recipientEmail.trim()}
                className="w-full bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50 gap-2"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send Invitation'}
              </Button>
              <p className="text-[11px] text-amber-800/60 text-center">
                Email is optional — we'll open your email app with the invite
                pre-filled. You can also just copy the link from the
                <span className="font-semibold"> Link</span> tab and share it
                anywhere (WhatsApp, SMS, AirDrop…).
              </p>

            </TabsContent>


            <TabsContent value="sms" className="space-y-3 pt-4">
              <div>
                <label className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                  Family member's mobile number
                </label>
                <Input
                  type="tel"
                  value={recipientPhone}
                  onChange={(event) =>
                    setRecipientPhone(
                      event.target.value,
                    )
                  }
                  placeholder="+1 601 555 0123"
                  className="mt-1 bg-white border-amber-300"
                />
              </div>
              <Button
                onClick={handleSendSms}
                disabled={
                  sending ||
                  !recipientPhone.trim() ||
                  (
                    effectiveInviteType ===
                      'SPONSORED_SEAT' &&
                    !resolvedTargetMemberId
                  )
                }
                className="w-full bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50 gap-2"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <MessageCircle className="w-4 h-4" />
                )}
                {sending
                  ? 'Preparing…'
                  : 'Send by SMS'}
              </Button>
              <p className="text-[11px] text-amber-800/60 text-center">
                KinsCircle records the exact recipient before opening your SMS app. A person-targeted claim still requires verified control of this phone number.
              </p>
            </TabsContent>

            <TabsContent value="contacts" className="space-y-3 pt-4">
              <p className="text-sm text-amber-900/80">
                Pick family members from your phone's contacts and we'll open your email or SMS app with the invite ready to send.
              </p>
              <div className="bg-white rounded-xl p-4 border border-amber-200 space-y-3">
                <Button
                  onClick={handleContacts}
                  className="w-full bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-800 hover:to-amber-950 text-amber-50 gap-2"
                >
                  <Users className="w-4 h-4" /> Open Contacts Picker
                </Button>
                {effectiveInviteType === 'FAMILY_PROFILE' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!confirmFamilyOnly()) return;

                        const body = encodeURIComponent(
                          `${inviterName || 'Your family'} invited you to KinsCircle: ${inviteLink}`,
                        );

                        window.location.href =
                          `sms:?&body=${body}`;

                        void recordInvite('share');
                      }}
                      className="border-amber-400 text-amber-900 hover:bg-amber-100 gap-1.5"
                    >
                      <MessageCircle className="w-4 h-4" />
                      SMS
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!confirmFamilyOnly()) return;

                        const text = encodeURIComponent(
                          `${inviterName || 'Your family'} invited you to KinsCircle: ${inviteLink}`,
                        );

                        window.open(
                          `https://wa.me/?text=${text}`,
                          '_blank',
                        );

                        void recordInvite('share');
                      }}
                      className="border-amber-400 text-amber-900 hover:bg-amber-100 gap-1.5"
                    >
                      <Share2 className="w-4 h-4" />
                      WhatsApp
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900">
                    Sponsored memberships must use
                    <b> Select One Contact</b> or
                    <b> Email</b>. Generic SMS and WhatsApp
                    sharing are disabled because they cannot bind
                    one paid seat to one verified recipient.
                  </div>
                )}

              </div>
              <p className="text-[11px] text-amber-800/60 text-center">
                Contacts picker works on mobile Chrome & Android. Desktop users: use the Link or Email tab.
              </p>
            </TabsContent>

            <TabsContent value="sent">
              <SentInvitesPanel refreshKey={sentRefreshKey} />
            </TabsContent>
          </Tabs>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteModal;
